import { Sorter } from "./sortable-collection";
import { SorterDefinitionDictionary, AccessorDefinitionDictionary } from "./../helpers/sorter-definition-dictionary";
import { SortDictionaryDefinition } from "./../helpers/sorter-definition-dictionary";
import {BaseRelationModel} from "../models/base-relation";

type Sortable = string | number;
export type Sorter = (a: any) => Sortable[];
export type Accessor = (a: any) => string;

export interface SortDictionary {
    Name: string;
    Comparator: Sorter;
    Accessor: Accessor;
}

/** Documentation regarding this class can be found in the Documentation Repo.
 * Specifically the SortingLists.md file
 */
export class SortableCollection<T extends BaseRelationModel> extends Backbone.Collection<BaseRelationModel> {

    public SortPriorities: string[] = [];
    private sorters: SortDictionary[] = [];
    public constructor(sorterDefinitions: SortDictionaryDefinition[], models?: T[]) {
        super();
        models.forEach((m) => {
            this.add(m);
        });
        this.setupSorters(sorterDefinitions);
        this.setInitialSortPriorities();
        if (this.first() && this.first().AlwaysShowTreatmentHeadings) {
            this.updateComparatorFromSortPriorities();
        }
    }

    public setupSorters(sorterDefinitions: SortDictionaryDefinition[]) {
        if (sorterDefinitions) {
            sorterDefinitions.forEach((d) => {

                const comparator = SorterDefinitionDictionary[d.Comparator];
                let accessor = AccessorDefinitionDictionary[d.Accessor];
                if (!accessor) {
                    accessor = () => "";
                }
                if (comparator) {
                    const sorter: SortDictionary = {
                        Name: d.Name,
                        Comparator: comparator,
                        Accessor: accessor
                    };
                    this.sorters.push(sorter);
                } else {
                    // tslint:disable-next-line
                    console.error("No comparator found with ID: " + d.Comparator + " Have you added it to the sorter-definition-dictionary?");
                }
            });

        }
    }

    public setInitialSortPriorities() {
        this.sorters.forEach((s) => {
            this.SortPriorities.push(s.Name);
        });
    }

    /** Create a new comparator to order the collection based on the SortPriorities
     *  This feature of backbone collections is undocumented, but there is some info here
     *  https://stackoverflow.com/questions/8921667/backbone-js-collection-comparator-sort-by-multiple-fields/10527900
     */
    public updateComparatorFromSortPriorities() {

        this.each((r) => {
            r.SemiCollapsed = false;
        });

        // Instead of our comparator taking an element and returning a number, it instead returns an array of values.
        // This array is a sorting order, so it will first sort each element by the first entry it its respective array, then by the second entry
        // So if we sort by treatment, then court we want to return an array [treatment, court]
        // These values we populate the arrays with must be strings or numbers in order for the sorting to work properly
        (this as any).comparator = (option) => {

            const sortByValues = [];
            $.each(this.SortPriorities, (index, item) => {
                const matchingSorter = _.findWhere(this.sorters, {Name: item}); // Find the sorter with the right name
                if (!matchingSorter) {
                    return;
                }

                const tempArr = this.secureSorter(matchingSorter, option); // !!IMPORTANT!! - Its vital you use secureSorter here
                // The secureSorter will apply the function but catch any errors in case a model is missing a specific property.

                for (const o of tempArr) {
                    sortByValues.push(o);
                }

            });
            return sortByValues;
        };
        this.sort();
        this.setHeadings();
    }

    public setHeadings() {
        const treatmentSorter = this.sorters.find((s) => s.Name === "Treatment");
        if (this.first() && this.first().AlwaysShowTreatmentHeadings && treatmentSorter ) {
            this.setHeadingsInternal(treatmentSorter.Accessor);
        } else {
            this.setHeadingsInternal();
        }

    }

    private setHeadingsInternal(subheadingAccessor?: Accessor) {
        const matchingSorter = _.findWhere(this.sorters, {Name: this.SortPriorities[0]}); // Find the sorter with the right name
        if (!matchingSorter) {
            return;
        }
        const accessor = matchingSorter.Accessor;
        const visibleRelations = this.where((rel) => rel.IsActuallyVisible);

        _.each(visibleRelations, (current) => {
            current.Heading = ""; // Start off with no headings
            current.SubHeading = "";
            const i = visibleRelations.indexOf(current);
            if (i === 0 ) { // First always has heading
                current.Heading = this.secureAccessor(accessor, current);
            } else {
                const previous = visibleRelations[i - 1];
                if (this.secureAccessor(accessor, current) !== this.secureAccessor(accessor, previous)) { // IF last heading is different to previous, show it
                    current.Heading = this.secureAccessor(accessor, current);
                }
            }

            if (subheadingAccessor && subheadingAccessor !== accessor) { // IF we're showing subheadings (and they aren't just a duplicate of main headings)
                if (i === 0 ) { // First always has heading
                    current.SubHeading = this.secureAccessor(subheadingAccessor, current);
                } else {
                    const previous = visibleRelations[i - 1];
                    if (this.secureAccessor(subheadingAccessor, current) !== this.secureAccessor(subheadingAccessor, previous)) { // IF last heading is different to previous, show it
                        current.SubHeading = this.secureAccessor(subheadingAccessor, current);
                    }
                }
            }

        });
    }

    /** This method runs a comparator wrapped in a try/catch. If it errors it will simply return massive number to put this at the end of the list */
    private secureSorter(d: SortDictionary, arg: any): Sortable[] {
        try {
            return d.Comparator(arg);
        } catch (e) {
            // tslint:disable-next-line
            console.error("Error applying the comparator name: " + d.Name + " Is the property present on this model?");
            dev.log("Massive number will be returned instead");
            return [9007199254740991];
        }
    }

    /** This method runs an accessor wrapped in a try/catch. If it errors it will simply return empty string */
    private secureAccessor(f: Accessor, arg: any): string {
        try {
            return f(arg);
        }
        catch (e) {
            // tslint:disable-next-line
            console.error("Error applying the accessor. Is the property present on this model?");
            dev.log("empty string will be returned instead");
            return "";
        }
    }

}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/collections/sortable-collection.ts